import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { LanguageProvider } from "./i18n";
import axios from "axios";
import { Toaster, toast } from "sonner";
import LandingPage from "./LandingPage";
import DocumentsPage from "./DocumentsPage";
import AboutPage from "./AboutPage";
import PaymentMethodsPage from "./PaymentMethodsPage";
import { 
  LayoutDashboard, 
  Users, 
  ArrowLeftRight, 
  Settings,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Wallet,
  UserCheck,
  Activity,
  DollarSign,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  ChevronRight,
  Smartphone,
  BarChart3,
  PieChart,
  AlertTriangle,
  Zap,
  Shield,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

import WalletApp from "./WalletApp";
import AdminApp from "./AdminApp";
import B2BDocumentsPage from "./B2BDocumentsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detect if running inside Telegram Web App
const isTelegramWebApp = () => {
  // Check for actual Telegram WebApp with valid initData
  const tg = window.Telegram?.WebApp;
  return tg && tg.initData && tg.initData.length > 0;
};

// Sidebar Component
const Sidebar = () => {
  const location = useLocation();
  
  const navItems = [
    { path: "/", icon: LayoutDashboard, label: "Мониторинг" },
    { path: "/analytics", icon: BarChart3, label: "Аналитика" },
    { path: "/transactions", icon: ArrowLeftRight, label: "Транзакции" },
    { path: "/users", icon: Users, label: "Пользователи" },
    { path: "/settings", icon: Settings, label: "Настройки" },
    { path: "/wallet", icon: Smartphone, label: "Превью кошелька" },
  ];

  return (
    <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 text-white flex flex-col z-50">
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
            <Wallet className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-semibold text-lg tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>
              PolyTech Wallet
            </h1>
            <p className="text-xs text-slate-400">Админ-панель</p>
          </div>
        </div>
      </div>
      
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive 
                      ? "bg-slate-800 text-white border-l-2 border-emerald-500" 
                      : "text-slate-400 hover:bg-slate-800/50 hover:text-white"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      
      <div className="p-4 border-t border-slate-800">
        <div className="text-xs text-slate-500 text-center">
          v1.0.0 • OxyPay
        </div>
      </div>
    </aside>
  );
};

// Header Component
const Header = ({ title, onRefresh, loading }) => {
  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/50 px-8 py-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>
          {title}
        </h1>
        {onRefresh && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onRefresh}
            disabled={loading}
            data-testid="refresh-button"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Обновить
          </Button>
        )}
      </div>
    </header>
  );
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = "slate" }) => {
  const colorClasses = {
    emerald: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    slate: "bg-slate-100 text-slate-600",
    red: "bg-red-50 text-red-600"
  };

  return (
    <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>
              {value}
            </p>
            {subtitle && (
              <p className="text-sm text-slate-500">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        {trend && (
          <div className={`flex items-center gap-1 mt-4 text-sm ${trend > 0 ? "text-emerald-600" : "text-red-600"}`}>
            {trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span>{Math.abs(trend)}% за сегодня</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Dashboard Page
const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [rate, setRate] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, rateRes, txRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`),
        axios.get(`${API}/rate`),
        axios.get(`${API}/transactions?limit=5`)
      ]);
      setAnalytics(analyticsRes.data);
      setRate(rateRes.data);
      setTransactions(txRes.data);
    } catch (e) {
      console.error(e);
      toast.error("Ошибка загрузки данных");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const formatCurrency = (value, currency = "₽") => {
    return new Intl.NumberFormat("ru-RU").format(value) + " " + currency;
  };

  const formatPercent = (value) => {
    const sign = value >= 0 ? "+" : "";
    return `${sign}${value}%`;
  };

  // Mini chart component for daily volume
  const MiniChart = ({ data }) => {
    if (!data || data.length === 0) return null;
    const maxValue = Math.max(...data.map(d => d.topup + d.withdraw));
    
    return (
      <div className="flex items-end gap-1 h-12">
        {data.map((day, idx) => {
          const height = maxValue > 0 ? ((day.topup + day.withdraw) / maxValue) * 100 : 0;
          return (
            <div
              key={idx}
              className="flex-1 bg-emerald-500 rounded-t transition-all hover:bg-emerald-400"
              style={{ height: `${Math.max(height, 5)}%` }}
              title={`${day._id}: ${formatCurrency(day.topup + day.withdraw)}`}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Мониторинг" onRefresh={fetchData} loading={loading} />
      
      <main className="p-8">
        {/* Top Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard 
            title="Всего пользователей"
            value={analytics?.users?.total || 0}
            subtitle={
              <div className="flex items-center gap-2">
                <span className={analytics?.users?.growth_percent >= 0 ? "text-emerald-600" : "text-red-600"}>
                  {formatPercent(analytics?.users?.growth_percent || 0)}
                </span>
                <span className="text-slate-400">за сутки</span>
              </div>
            }
            icon={Users}
            color="blue"
          />
          <StatCard 
            title="Общий баланс"
            value={formatCurrency(analytics?.balance?.total_usdt?.toFixed(2) || 0, "USDT")}
            subtitle={`≈ ${formatCurrency(analytics?.balance?.total_rub || 0)}`}
            icon={Wallet}
            color="emerald"
          />
          <StatCard 
            title="Оборот сегодня"
            value={formatCurrency(analytics?.volume?.today_topup_rub || 0)}
            subtitle={
              <div className="flex items-center gap-1">
                {analytics?.volume?.net_today >= 0 ? (
                  <ArrowUpRight className="w-4 h-4 text-emerald-600" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-red-600" />
                )}
                <span className={analytics?.volume?.net_today >= 0 ? "text-emerald-600" : "text-red-600"}>
                  {formatCurrency(Math.abs(analytics?.volume?.net_today || 0))}
                </span>
                <span className="text-slate-400">нетто</span>
              </div>
            }
            icon={TrendingUp}
            color="emerald"
          />
          <StatCard 
            title="Транзакций сегодня"
            value={analytics?.transactions?.today_count || 0}
            subtitle={
              analytics?.transactions?.pending_count > 0 ? (
                <div className="flex items-center gap-1 text-amber-600">
                  <Clock className="w-3 h-3" />
                  <span>{analytics.transactions.pending_count} в обработке</span>
                </div>
              ) : (
                <span className="text-emerald-600">Все выполнены</span>
              )
            }
            icon={Activity}
            color="slate"
          />
        </div>

        {/* Secondary Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Верифицированы</p>
                  <p className="text-2xl font-bold text-slate-900">{analytics?.users?.kyc_verified || 0}</p>
                </div>
                <UserCheck className="w-8 h-8 text-emerald-500" />
              </div>
              <Progress 
                value={analytics?.users?.total > 0 ? (analytics.users.kyc_verified / analytics.users.total) * 100 : 0} 
                className="mt-2 h-1"
              />
            </CardContent>
          </Card>
          
          <Card className="bg-white border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Новые сегодня</p>
                  <p className="text-2xl font-bold text-slate-900">{analytics?.users?.new_today || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-blue-500" />
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Вчера: {analytics?.users?.new_yesterday || 0}
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Активных (7д)</p>
                  <p className="text-2xl font-bold text-slate-900">{analytics?.users?.active_week || 0}</p>
                </div>
                <Zap className="w-8 h-8 text-amber-500" />
              </div>
              <Progress 
                value={analytics?.users?.total > 0 ? (analytics.users.active_week / analytics.users.total) * 100 : 0} 
                className="mt-2 h-1"
              />
            </CardContent>
          </Card>
          
          <Card className="bg-white border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">С PIN-кодом</p>
                  <p className="text-2xl font-bold text-slate-900">{analytics?.users?.with_pin || 0}</p>
                </div>
                <Shield className="w-8 h-8 text-slate-500" />
              </div>
              <Progress 
                value={analytics?.users?.total > 0 ? (analytics.users.with_pin / analytics.users.total) * 100 : 0} 
                className="mt-2 h-1"
              />
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Volume Chart Card */}
          <Card className="lg:col-span-2 bg-white border border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                    Объём за неделю
                  </CardTitle>
                  <CardDescription>
                    Итого: {formatCurrency(analytics?.volume?.week_topup_rub || 0)}
                  </CardDescription>
                </div>
                <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Пополнения
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-40 flex items-end gap-2 px-2">
                {analytics?.chart_data?.daily_volume?.map((day, idx) => {
                  const maxVal = Math.max(
                    ...((analytics?.chart_data?.daily_volume || []).map(d => Math.max(d.topup, d.withdraw)))
                  );
                  const topupH = maxVal > 0 ? (day.topup / maxVal) * 100 : 0;
                  const withdrawH = maxVal > 0 ? (day.withdraw / maxVal) * 100 : 0;
                  const dayName = new Date(day._id).toLocaleDateString('ru-RU', { weekday: 'short' });
                  
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex gap-1 h-32 items-end">
                        <div
                          className="flex-1 bg-emerald-500 rounded-t transition-all hover:bg-emerald-400"
                          style={{ height: `${Math.max(topupH, 2)}%` }}
                          title={`Пополнения: ${formatCurrency(day.topup)}`}
                        />
                        <div
                          className="flex-1 bg-amber-500 rounded-t transition-all hover:bg-amber-400"
                          style={{ height: `${Math.max(withdrawH, 2)}%` }}
                          title={`Выводы: ${formatCurrency(day.withdraw)}`}
                        />
                      </div>
                      <span className="text-xs text-slate-500">{dayName}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-emerald-500" />
                  <span className="text-slate-600">Пополнения</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-amber-500" />
                  <span className="text-slate-600">Выводы</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rate & Quick Stats */}
          <div className="space-y-6">
            {/* Current Rate */}
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                  Текущий курс
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg">
                  <span className="text-slate-600 font-medium">1 USDT</span>
                  <span className="text-3xl font-bold text-emerald-600" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {rate?.usdt_rub_rate || 95} ₽
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Покупка</p>
                    <p className="text-lg font-semibold text-slate-900">{rate?.buy_fee_percent || 1.7}%</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Продажа</p>
                    <p className="text-lg font-semibold text-slate-900">{rate?.sell_fee_percent || 1.7}%</p>
                  </div>
                </div>
                <Link to="/settings">
                  <Button variant="outline" className="w-full mt-4" size="sm">
                    <Settings className="w-4 h-4 mr-2" />
                    Настроить курс
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Transaction Status */}
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                  Статусы транзакций
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(analytics?.transactions?.by_status || {}).map(([status, count]) => {
                  const statusConfig = {
                    COMPLETED: { label: "Выполнено", color: "bg-emerald-500" },
                    PENDING_PAYMENT: { label: "Ожидание", color: "bg-amber-500" },
                    PROCESSING: { label: "Обработка", color: "bg-blue-500" },
                    FAILED: { label: "Ошибка", color: "bg-red-500" },
                    CANCELED: { label: "Отменено", color: "bg-slate-400" }
                  };
                  const config = statusConfig[status] || { label: status, color: "bg-slate-400" };
                  const total = Object.values(analytics?.transactions?.by_status || {}).reduce((a, b) => a + b, 0);
                  const percent = total > 0 ? (count / total) * 100 : 0;
                  
                  return (
                    <div key={status} className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${config.color}`} />
                      <span className="text-sm text-slate-600 flex-1">{config.label}</span>
                      <span className="text-sm font-mono font-medium">{count}</span>
                      <span className="text-xs text-slate-400 w-12 text-right">{percent.toFixed(0)}%</span>
                    </div>
                  );
                })}
                
                {analytics?.transactions?.failed_today > 0 && (
                  <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {analytics.transactions.failed_today} ошибок сегодня
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recent Transactions */}
        <Card className="mt-6 bg-white border border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                Последние транзакции
              </CardTitle>
              <CardDescription>В реальном времени</CardDescription>
            </div>
            <Link to="/transactions">
              <Button variant="ghost" size="sm">
                Все транзакции
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Нет транзакций</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.map((tx) => (
                  <TransactionRow key={tx.id} tx={tx} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Transaction Row Component
const TransactionRow = ({ tx }) => {
  const typeConfig = {
    TOPUP_RUB_SBP: { label: "Пополнение", icon: TrendingUp, color: "emerald" },
    PAYOUT_RUB_TO_CARD: { label: "Вывод", icon: CreditCard, color: "amber" },
    EXCHANGE_RUB_TO_USDT: { label: "Обмен RUB→USDT", icon: ArrowLeftRight, color: "blue" },
    EXCHANGE_USDT_TO_RUB: { label: "Обмен USDT→RUB", icon: ArrowLeftRight, color: "blue" }
  };

  const statusConfig = {
    COMPLETED: { label: "Выполнено", icon: CheckCircle, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    PENDING_PAYMENT: { label: "Ожидание", icon: Clock, color: "bg-amber-50 text-amber-700 border-amber-200" },
    PROCESSING: { label: "Обработка", icon: Clock, color: "bg-blue-50 text-blue-700 border-blue-200" },
    PROCESSING_PAYOUT: { label: "Обработка выплаты", icon: Clock, color: "bg-blue-50 text-blue-700 border-blue-200" },
    FAILED: { label: "Ошибка", icon: XCircle, color: "bg-red-50 text-red-700 border-red-200" },
    CANCELED: { label: "Отменено", icon: XCircle, color: "bg-slate-100 text-slate-700 border-slate-200" }
  };

  const type = typeConfig[tx.type] || { label: tx.type, icon: Activity, color: "slate" };
  const status = statusConfig[tx.status] || { label: tx.status, icon: Clock, color: "bg-slate-100 text-slate-700" };
  const StatusIcon = status.icon;

  return (
    <div className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50/50 transition-colors">
      <div className="flex items-center gap-4">
        <div className={`p-2 rounded-lg bg-${type.color}-50`}>
          <type.icon className={`w-5 h-5 text-${type.color}-600`} />
        </div>
        <div>
          <p className="font-medium text-slate-900">{type.label}</p>
          <p className="text-sm text-slate-500" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            ID: {tx.telegram_id}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className="font-semibold text-slate-900" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          {tx.amount_rub?.toFixed(0) || 0} ₽ / {tx.amount_usdt?.toFixed(4) || 0} USDT
        </p>
        <Badge className={`${status.color} border`}>
          <StatusIcon className="w-3 h-3 mr-1" />
          {status.label}
        </Badge>
      </div>
    </div>
  );
};

// Transactions Page
const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/transactions?limit=50`);
      setTransactions(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Ошибка загрузки транзакций");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const statusConfig = {
    COMPLETED: { label: "Выполнено", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    PENDING_PAYMENT: { label: "Ожидание", color: "bg-amber-50 text-amber-700 border-amber-200" },
    PROCESSING: { label: "Обработка", color: "bg-blue-50 text-blue-700 border-blue-200" },
    PROCESSING_PAYOUT: { label: "Выплата", color: "bg-blue-50 text-blue-700 border-blue-200" },
    FAILED: { label: "Ошибка", color: "bg-red-50 text-red-700 border-red-200" },
    CANCELED: { label: "Отменено", color: "bg-slate-100 text-slate-700 border-slate-200" },
    CREATED: { label: "Создано", color: "bg-slate-100 text-slate-700 border-slate-200" }
  };

  const typeNames = {
    TOPUP_RUB_SBP: "Пополнение",
    TOPUP_CARD: "Пополнение Visa/MC",
    TOPUP_MIA: "Пополнение MIA",
    PAYOUT_RUB_TO_CARD: "Вывод на карту",
    PAYOUT_CARD: "Вывод Visa/MC",
    EXCHANGE_RUB_TO_USDT: "Обмен",
    EXCHANGE_USDT_TO_RUB: "Обмен",
    EXCHANGE: "Обмен валют"
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Транзакции" onRefresh={fetchData} loading={loading} />
      
      <main className="p-8">
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">ID</TableHead>
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">Telegram ID</TableHead>
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">Тип</TableHead>
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">Сумма RUB</TableHead>
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">Сумма USDT</TableHead>
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">Статус</TableHead>
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">Дата</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Нет транзакций
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((tx) => {
                    const status = statusConfig[tx.status] || { label: tx.status, color: "bg-slate-100 text-slate-700" };
                    return (
                      <TableRow key={tx.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <TableCell className="py-4 px-6 text-sm font-mono text-slate-600">
                          {tx.id.slice(0, 8)}...
                        </TableCell>
                        <TableCell className="py-4 px-6 text-sm font-mono">
                          {tx.telegram_id}
                        </TableCell>
                        <TableCell className="py-4 px-6 text-sm text-slate-700">
                          {typeNames[tx.type] || tx.type}
                        </TableCell>
                        <TableCell className="py-4 px-6 text-sm font-mono">
                          {tx.amount_rub?.toFixed(2) || "—"} ₽
                        </TableCell>
                        <TableCell className="py-4 px-6 text-sm font-mono">
                          {tx.amount_usdt?.toFixed(4) || "—"}
                        </TableCell>
                        <TableCell className="py-4 px-6">
                          <Badge className={`${status.color} border`}>
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-4 px-6 text-sm text-slate-500">
                          {new Date(tx.created_at).toLocaleString("ru-RU")}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Analytics Page
const AnalyticsPage = () => {
  const [usersAnalytics, setUsersAnalytics] = useState(null);
  const [txAnalytics, setTxAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, txRes] = await Promise.all([
        axios.get(`${API}/analytics/users`),
        axios.get(`${API}/analytics/transactions?days=7`)
      ]);
      setUsersAnalytics(usersRes.data);
      setTxAnalytics(txRes.data);
    } catch (e) {
      console.error(e);
      toast.error("Ошибка загрузки аналитики");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (value, currency = "₽") => {
    return new Intl.NumberFormat("ru-RU").format(value) + " " + currency;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Аналитика" onRefresh={fetchData} loading={loading} />
      
      <main className="p-8">
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="users" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
              <Users className="w-4 h-4 mr-2" />
              Пользователи
            </TabsTrigger>
            <TabsTrigger value="transactions" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
              <ArrowLeftRight className="w-4 h-4 mr-2" />
              Транзакции
            </TabsTrigger>
          </TabsList>
          
          {/* Users Analytics Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Users by Balance */}
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-emerald-500" />
                    Топ пользователей по балансу
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {usersAnalytics?.top_by_balance?.map((user, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-semibold text-sm">
                            {idx + 1}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {user.username ? `@${user.username}` : user.first_name || `ID: ${user.telegram_id}`}
                            </p>
                            <p className="text-xs text-slate-500">
                              {user.kyc_status === 'APPROVED' ? (
                                <span className="text-emerald-600 flex items-center gap-1">
                                  <UserCheck className="w-3 h-3" /> Верифицирован
                                </span>
                              ) : (
                                <span className="text-slate-400">Не верифицирован</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-900 font-mono">
                            {user.balance_usdt?.toFixed(2)} USDT
                          </p>
                        </div>
                      </div>
                    ))}
                    {(!usersAnalytics?.top_by_balance || usersAnalytics.top_by_balance.length === 0) && (
                      <div className="text-center py-8 text-slate-500">
                        <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Нет данных</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* KYC Distribution */}
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-blue-500" />
                    Распределение по KYC
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(usersAnalytics?.kyc_distribution || {}).map(([status, count]) => {
                      const total = Object.values(usersAnalytics?.kyc_distribution || {}).reduce((a, b) => a + b, 0);
                      const percent = total > 0 ? (count / total) * 100 : 0;
                      const config = {
                        NOT_STARTED: { label: "Не начата", color: "bg-slate-200", textColor: "text-slate-600" },
                        PENDING: { label: "На проверке", color: "bg-amber-400", textColor: "text-amber-600" },
                        APPROVED: { label: "Подтверждена", color: "bg-emerald-500", textColor: "text-emerald-600" },
                        REJECTED: { label: "Отклонена", color: "bg-red-400", textColor: "text-red-600" }
                      };
                      const cfg = config[status] || { label: status, color: "bg-slate-200", textColor: "text-slate-600" };
                      
                      return (
                        <div key={status}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-sm font-medium ${cfg.textColor}`}>{cfg.label}</span>
                            <span className="text-sm text-slate-500">{count} ({percent.toFixed(1)}%)</span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-3">
                            <div className={`${cfg.color} h-3 rounded-full transition-all`} style={{ width: `${percent}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Registration Chart */}
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-slate-500" />
                  Регистрации за последние 30 дней
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-40 flex items-end gap-1">
                  {usersAnalytics?.registration_chart?.map((day, idx) => {
                    const maxVal = Math.max(...((usersAnalytics?.registration_chart || []).map(d => d.count)));
                    const height = maxVal > 0 ? (day.count / maxVal) * 100 : 0;
                    
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center">
                        <div
                          className="w-full bg-blue-500 rounded-t hover:bg-blue-400 transition-all cursor-pointer"
                          style={{ height: `${Math.max(height, 2)}%` }}
                          title={`${day._id}: ${day.count} регистраций`}
                        />
                      </div>
                    );
                  })}
                </div>
                {(!usersAnalytics?.registration_chart || usersAnalytics.registration_chart.length === 0) && (
                  <div className="text-center py-8 text-slate-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Нет данных о регистрациях</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Transactions Analytics Tab */}
          <TabsContent value="transactions" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Успешность</p>
                      <p className="text-3xl font-bold text-emerald-600">{txAnalytics?.success_rate || 0}%</p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-emerald-500" />
                  </div>
                  <Progress value={txAnalytics?.success_rate || 0} className="mt-4 h-2" />
                </CardContent>
              </Card>
              
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Выполнено</p>
                      <p className="text-3xl font-bold text-slate-900">{txAnalytics?.completed || 0}</p>
                    </div>
                    <Activity className="w-10 h-10 text-blue-500" />
                  </div>
                  <p className="text-sm text-slate-400 mt-2">
                    из {txAnalytics?.total_transactions || 0} за период
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Ошибки</p>
                      <p className="text-3xl font-bold text-red-600">{txAnalytics?.failed || 0}</p>
                    </div>
                    <XCircle className="w-10 h-10 text-red-500" />
                  </div>
                  <p className="text-sm text-slate-400 mt-2">
                    требуют внимания
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {/* Average by Type */}
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-slate-500" />
                  Средние показатели по типам операций
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(txAnalytics?.average_by_type || {}).map(([type, data]) => {
                    const typeNames = {
                      TOPUP_RUB_SBP: { label: "Пополнение", icon: TrendingUp, color: "emerald" },
                      TOPUP_CARD: { label: "Visa/MC", icon: TrendingUp, color: "emerald" },
                      TOPUP_MIA: { label: "MIA QR", icon: TrendingUp, color: "emerald" },
                      PAYOUT_RUB_TO_CARD: { label: "Вывод на карту", icon: CreditCard, color: "amber" },
                      PAYOUT_CARD: { label: "Вывод Visa/MC", icon: CreditCard, color: "amber" },
                      EXCHANGE_RUB_TO_USDT: { label: "Обмен", icon: ArrowLeftRight, color: "blue" },
                      EXCHANGE_USDT_TO_RUB: { label: "Обмен", icon: ArrowLeftRight, color: "blue" },
                      EXCHANGE: { label: "Обмен валют", icon: ArrowLeftRight, color: "blue" }
                    };
                    const cfg = typeNames[type] || { label: type, icon: Activity, color: "slate" };
                    
                    return (
                      <div key={type} className={`p-4 rounded-lg bg-${cfg.color}-50 border border-${cfg.color}-100`}>
                        <div className="flex items-center gap-2 mb-2">
                          <cfg.icon className={`w-4 h-4 text-${cfg.color}-600`} />
                          <span className="text-sm font-medium text-slate-700">{cfg.label}</span>
                        </div>
                        <p className="text-2xl font-bold text-slate-900 font-mono">
                          {formatCurrency(data.avg_rub)}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {data.count} операций
                        </p>
                      </div>
                    );
                  })}
                </div>
                {(!txAnalytics?.average_by_type || Object.keys(txAnalytics.average_by_type).length === 0) && (
                  <div className="text-center py-8 text-slate-500">
                    <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Нет данных о транзакциях</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Hourly Distribution */}
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-500" />
                  Распределение по часам (сегодня)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-32 flex items-end gap-1">
                  {Array.from({ length: 24 }, (_, hour) => {
                    const data = txAnalytics?.hourly_distribution?.find(d => d._id === hour);
                    const count = data?.count || 0;
                    const maxVal = Math.max(...((txAnalytics?.hourly_distribution || []).map(d => d.count)), 1);
                    const height = (count / maxVal) * 100;
                    
                    return (
                      <div key={hour} className="flex-1 flex flex-col items-center">
                        <div
                          className="w-full bg-slate-300 hover:bg-slate-400 rounded-t transition-all cursor-pointer"
                          style={{ height: `${Math.max(height, 2)}%`, backgroundColor: count > 0 ? '#10B981' : '#E2E8F0' }}
                          title={`${hour}:00 - ${count} транзакций`}
                        />
                        {hour % 4 === 0 && (
                          <span className="text-xs text-slate-400 mt-1">{hour}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// Users Page
const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users?limit=50`);
      setUsers(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Ошибка загрузки пользователей");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const kycConfig = {
    NOT_STARTED: { label: "Не пройдена", color: "bg-slate-100 text-slate-700 border-slate-200" },
    PENDING: { label: "На проверке", color: "bg-amber-50 text-amber-700 border-amber-200" },
    APPROVED: { label: "Подтверждена", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    REJECTED: { label: "Отклонена", color: "bg-red-50 text-red-700 border-red-200" }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Пользователи" onRefresh={fetchData} loading={loading} />
      
      <main className="p-8">
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">Telegram ID</TableHead>
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">Username</TableHead>
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">Имя</TableHead>
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">Баланс USDT</TableHead>
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">Зарезервировано</TableHead>
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">KYC</TableHead>
                  <TableHead className="text-slate-500 uppercase text-xs font-medium tracking-wider">Регистрация</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Нет пользователей
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => {
                    const kyc = kycConfig[user.kyc_status] || { label: user.kyc_status, color: "bg-slate-100 text-slate-700" };
                    return (
                      <TableRow key={user.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <TableCell className="py-4 px-6 text-sm font-mono">
                          {user.telegram_id}
                        </TableCell>
                        <TableCell className="py-4 px-6 text-sm">
                          {user.username ? `@${user.username}` : "—"}
                        </TableCell>
                        <TableCell className="py-4 px-6 text-sm">
                          {user.first_name || "—"}
                        </TableCell>
                        <TableCell className="py-4 px-6 text-sm font-mono font-medium text-emerald-600">
                          {user.balance_usdt?.toFixed(4)}
                        </TableCell>
                        <TableCell className="py-4 px-6 text-sm font-mono text-amber-600">
                          {user.reserved_usdt?.toFixed(4) || "0.0000"}
                        </TableCell>
                        <TableCell className="py-4 px-6">
                          <Badge className={`${kyc.color} border`}>
                            {user.kyc_status === "APPROVED" && <UserCheck className="w-3 h-3 mr-1" />}
                            {kyc.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-4 px-6 text-sm text-slate-500">
                          {new Date(user.created_at).toLocaleDateString("ru-RU")}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Settings Page
const SettingsPage = () => {
  const [rate, setRate] = useState({
    usdt_rub_rate: 95,
    buy_fee_percent: 1.7,
    sell_fee_percent: 1.7
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rate`);
      setRate(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Ошибка загрузки настроек");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/rate`, rate);
      toast.success("Настройки сохранены");
      setDialogOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Настройки" onRefresh={fetchData} loading={loading} />
      
      <main className="p-8 max-w-2xl">
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle style={{ fontFamily: "'Work Sans', sans-serif" }}>Курс обмена</CardTitle>
            <CardDescription>Настройки курса и комиссий</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="rate">Курс USDT/RUB</Label>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">1 USDT =</span>
                <Input
                  id="rate"
                  type="number"
                  step="0.01"
                  value={rate.usdt_rub_rate}
                  onChange={(e) => setRate({ ...rate, usdt_rub_rate: parseFloat(e.target.value) || 0 })}
                  className="w-32 font-mono"
                  data-testid="rate-input"
                />
                <span className="text-slate-500">₽</span>
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-2">
              <Label htmlFor="buyFee">Комиссия покупки (%)</Label>
              <Input
                id="buyFee"
                type="number"
                step="0.1"
                value={rate.buy_fee_percent}
                onChange={(e) => setRate({ ...rate, buy_fee_percent: parseFloat(e.target.value) || 0 })}
                className="w-32 font-mono"
                data-testid="buy-fee-input"
              />
              <p className="text-sm text-slate-500">Комиссия при пополнении RUB → USDT</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="sellFee">Комиссия продажи (%)</Label>
              <Input
                id="sellFee"
                type="number"
                step="0.1"
                value={rate.sell_fee_percent}
                onChange={(e) => setRate({ ...rate, sell_fee_percent: parseFloat(e.target.value) || 0 })}
                className="w-32 font-mono"
                data-testid="sell-fee-input"
              />
              <p className="text-sm text-slate-500">Комиссия при выводе USDT → RUB</p>
            </div>
            
            <Separator />
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full" data-testid="save-settings-button">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Сохранить настройки
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Подтверждение</DialogTitle>
                  <DialogDescription>
                    Вы уверены, что хотите применить новые настройки курса?
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Курс:</span>
                    <span className="font-mono font-medium">{rate.usdt_rub_rate} ₽</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Комиссия покупки:</span>
                    <span className="font-mono font-medium">{rate.buy_fee_percent}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Комиссия продажи:</span>
                    <span className="font-mono font-medium">{rate.sell_fee_percent}%</span>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Отмена
                  </Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="confirm-save-button">
                    {saving ? "Сохранение..." : "Подтвердить"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Wallet Preview Page (shows mobile wallet interface)
const WalletPreviewPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Превью кошелька" />
      <main className="p-8">
        <div className="max-w-md mx-auto">
          <Card className="bg-white border border-slate-200 shadow-lg overflow-hidden">
            <div className="aspect-[9/16] overflow-auto">
              <WalletApp />
            </div>
          </Card>
          <p className="text-center text-slate-500 text-sm mt-4">
            Так выглядит интерфейс в Telegram Mini App
          </p>
        </div>
      </main>
    </div>
  );
};

// Main App
function App() {
  const pathname = window.location.pathname;
  
  // If URL path is /admin, show AdminApp
  if (pathname === '/admin' || pathname === '/admin/') {
    return <AdminApp />;
  }
  
  // If URL path is /wallet, show WalletApp (for Telegram Mini App)
  if (pathname === '/wallet' || pathname === '/wallet/') {
    return (
      <LanguageProvider>
        <WalletApp />
      </LanguageProvider>
    );
  }
  
  // If URL path is /documents, show DocumentsPage
  if (pathname === '/documents' || pathname === '/documents/') {
    return <DocumentsPage />;
  }
  
  // If URL path is /about, show AboutPage
  if (pathname === '/about' || pathname === '/about/') {
    return (
      <LanguageProvider>
        <AboutPage />
      </LanguageProvider>
    );
  }
  
  // If URL path is /payment-methods, show PaymentMethodsPage
  if (pathname === '/payment-methods' || pathname === '/payment-methods/') {
    return (
      <LanguageProvider>
        <PaymentMethodsPage />
      </LanguageProvider>
    );
  }
  
  // If URL path is /b2b/documents, show B2B Documents
  if (pathname === '/b2b/documents' || pathname === '/b2b/documents/' || pathname === '/pro/documents' || pathname === '/pro/documents/') {
    return <B2BDocumentsPage />;
  }
  
  // Check if running as Telegram Web App - show wallet
  const tg = window.Telegram?.WebApp;
  if (tg && tg.initData && tg.initData.length > 0) {
    return (
      <LanguageProvider>
        <WalletApp />
      </LanguageProvider>
    );
  }
  
  // Default: show Landing Page with language support
  return (
    <LanguageProvider>
      <LandingPage />
    </LanguageProvider>
  );
}

export default App;
